import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../configs/api";
import ImageCarousel from "../../components/AdminSideComponents/common/ImageCarousel";
import { BANNER_IMAGE } from "../../utils/constants";

const AdminProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProduct = async () => {
    try {
      setLoading(true);

      const res = await api.get(`/products/${id}`);
      const data = res.data.data;

      setProduct(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProduct();
    window.scrollTo({ top: 0 });
  }, [id]);

  if (loading) return <ProductDetailsSkeleton />;

  return (
    <>
      {/* HERO BANNER */}
      <div
        className="relative w-full h-[10rem] sm:h-[13rem] md:h-[16rempx]  flex items-center"
        style={{
          backgroundImage: `url(${BANNER_IMAGE})`,
          backgroundSize: "cover",
          backgroundPosition: "bottom",
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/20" />

        {/* Content */}
        <div className="relative z-10 w-full px-6 md:px-8 lg:px-10 text-white">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-heading font-semibold tracking-wider line-clamp-1 mb-2">
            {/* {product?.name} */}
            Product Details
          </h1>

          <div className="text-xs sm:text-sm flex items-center gap-2 text-gray-200">
            <span
              className="cursor-pointer hover:text-white"
              onClick={() => navigate("/admin/dashboard")}
            >
              Dashboard
            </span>

            <span>/</span>

            <span
              className="cursor-pointer hover:text-white"
              onClick={() => navigate("/admin/products")}
            >
              Products
            </span>

            <span>/</span>

            <span className="text-white font-medium">{product?.name}</span>
          </div>
        </div>
      </div>
      <div className="w-full max-w-5xl  px-4 sm:px-6 md:px-8 lg:px-10 py-6 lg:pt-10  lg:pb-16">
        <div className="">
          {/* TITLE */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold font-heading text-headingText mb-1.5 sm:mb-3">
            {product?.name}
          </h1>

          <p className="text-descriptionText text-base lg:text-lg mb-6">
            {product?.tagline}
          </p>

          <div className="mb-6 sm:mb-8 w-full aspect-square sm:aspect-auto  sm:h-[33rem] shadow-xl overflow-hidden ">
            <ImageCarousel
              images={product.images?.map((img) => ({
                imageUrl: img,
                title: product.name,
              }))}
              originalSize={true}
            />{" "}
          </div>

          {/* DESCRIPTION */}
          <div className="mb-8">
            <h2 className="text-2xl font-heading font-semibold uppercase text-headingText mb-4">
              Product Overview
            </h2>

            <p className="text-descriptionText leading-relaxed text-justify tracking-wide">
              {product?.description}
            </p>
          </div>

          {/* FEATURES */}
          <div className="mb-8">
            <h2 className="text-2xl font-heading uppercase font-semibold text-headingText mb-4">
              Key Features
            </h2>

            <ul className="flex flex-col gap-3">
              {product?.features?.map((feature, i) => (
                <li
                  key={i}
                  className="flex items-center  gap-2 text-descriptionText text-justify"
                >
                  <div className="size-1.5 bg-primaryAccent shrink-0"></div>{" "}
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* CLOSING TEXT */}
          {product?.closingText && (
            <div className="bg-surfaceAccentLight p-3 sm:p-6 rounded-lg border border-borderLight">
              <p className="text-descriptionText leading-relaxed tracking-wide text-justify">
                {product.closingText}
              </p>
            </div>
          )}
        </div>
      </div>{" "}
    </>
  );
};

const ProductDetailsSkeleton = () => {
  return (
    <div className="w-full animate-pulse">
      {/* HERO BANNER */}
      <div className="relative w-full h-[10rem] sm:h-[13rem] md:h-[16rem] bg-divider">
        <div className="absolute bottom-4 sm:bottom-6 left-6 md:left-8 lg:left-10 space-y-2">
          <div className="h-6 sm:h-8 w-40 sm:w-56 bg-gray-300 rounded" />
          <div className="h-3 w-32 bg-gray-300 rounded" />
        </div>
      </div>

      {/* PAGE CONTENT */}
      <div className="w-full max-w-5xl px-4 sm:px-6 md:px-8 lg:px-10 py-6 lg:pt-10 lg:pb-16 space-y-8">
        {/* TITLE + TAGLINE */}
        <div className="space-y-3">
          <div className="h-8 sm:h-10 w-56 sm:w-72 bg-divider rounded" />
          <div className="h-4 w-64 bg-divider rounded" />
        </div>

        {/* IMAGE CAROUSEL */}
        <div className="w-full aspect-square sm:aspect-auto sm:h-[33rem] bg-divider" />

        {/* PRODUCT OVERVIEW */}
        <div className="space-y-4">
          <div className="h-6 w-40 bg-divider rounded" />

          <div className="space-y-2">
            <div className="h-4 w-full bg-divider rounded" />
            <div className="h-4 w-full bg-divider rounded" />
            <div className="h-4 w-5/6 bg-divider rounded" />
            <div className="h-4 w-3/4 bg-divider rounded" />
          </div>
        </div>

        {/* FEATURES */}
        <div className="space-y-4">
          <div className="h-6 w-36 bg-divider rounded" />

          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="size-1.5 bg-gray-300 rounded-full" />
                <div className="h-4 w-full bg-divider rounded" />
              </div>
            ))}
          </div>
        </div>

        {/* CLOSING TEXT CARD */}
        <div className="bg-divider border border-borderLight rounded-lg p-6 space-y-2">
          <div className="h-4 w-full bg-gray-300 rounded" />
          <div className="h-4 w-5/6 bg-gray-300 rounded" />
          <div className="h-4 w-3/4 bg-gray-300 rounded" />
        </div>
      </div>
    </div>
  );
};

export default AdminProductDetails;
